<template>
  <div class="app-textarea-ai-wrapper" :class="{ 'has-ai': enableAi }">
    <div class="textarea-container" @mouseenter="showAiButton = true" @mouseleave="showAiButton = false">
      <textarea
        :id="id"
        :value="value"
        @input="handleInput"
        :maxlength="maxChars"
        :rows="rows"
        :placeholder="placeholder"
        :class="textareaClass"
        :disabled="disabled"
        ref="textarea"
      ></textarea>
      
      <!-- AI Button -->
      <button
        v-if="enableAi && value && value.trim().length > 0"
        type="button"
        class="ai-button"
        :class="{ 'visible': showAiButton || isLoading }"
        @click="handleAiClick"
        :disabled="isLoading || !value || !value.trim()"
        v-b-tooltip.hover
        title="Mejorar texto con IA"
      >
        <span v-if="isLoading" class="spinner-border spinner-border-sm" aria-label="Cargando"></span>
        <i v-else class="mdi mdi-auto-fix" aria-hidden="true"></i>
      </button>
    </div>

    <!-- Comparison Modal -->
    <b-modal
      :id="modalId"
      ref="comparisonModal"
      title="Comparación de Texto"
      size="xl"
      centered
      @hidden="resetModal"
    >
      <template #default>
        <div class="comparison-container">
          <div class="comparison-panel">
            <h6 class="panel-title">
              <i class="mdi mdi-file-document-outline mr-2"></i>
              Texto Original
            </h6>
            <div class="text-preview original-text">
              {{ originalText }}
            </div>
            <small class="text-muted">{{ originalText.length }} caracteres</small>
          </div>
          
          <div class="comparison-divider">
            <i class="mdi mdi-arrow-right"></i>
          </div>
          
          <div class="comparison-panel">
            <h6 class="panel-title">
              <i class="mdi mdi-auto-fix mr-2"></i>
              Versión Sugerida
            </h6>
            <div class="text-preview suggested-text">
              {{ suggestedText }}
            </div>
            <small class="text-muted">{{ suggestedText.length }} caracteres</small>
          </div>
        </div>
      </template>
      
      <template #modal-footer>
        <div class="w-100 d-flex justify-content-between">
          <b-button variant="secondary" @click="discardSuggestion">
            <i class="mdi mdi-close mr-2"></i>
            Descartar
          </b-button>
          <b-button variant="success" @click="applySuggestion">
            <i class="mdi mdi-check mr-2"></i>
            Aplicar Sugerencia
          </b-button>
        </div>
      </template>
    </b-modal>
  </div>
</template>

<script>
import api from "@/helpers/api-rest/api.js";
import Swal from "sweetalert2";

export default {
  name: "AppTextareaAi",
  props: {
    value: {
      type: String,
      default: "",
    },
    enableAi: {
      type: Boolean,
      default: false,
    },
    maxChars: {
      type: Number,
      default: null,
    },
    rows: {
      type: Number,
      default: 3,
    },
    placeholder: {
      type: String,
      default: "",
    },
    textareaClass: {
      type: [String, Object, Array],
      default: "form-control",
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    id: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      showAiButton: false,
      isLoading: false,
      originalText: "",
      suggestedText: "",
      modalId: `ai-comparison-modal-${Math.random().toString(36).substr(2, 9)}`,
    };
  },
  methods: {
    handleInput(event) {
      this.$emit("input", event.target.value);
    },
    async handleAiClick() {
      if (!this.value || !this.value.trim()) {
        return;
      }

      this.isLoading = true;
      this.originalText = this.value;

      try {
        const response = await api.post({
          url: "/api/v1/text-optimization/improve",
          data: {
            text: this.value,
            max_chars: this.maxChars,
          },
          withLoading: false, // No mostrar loading global para esta petición
        });

        // La API devuelve { data: { optimized_text: "..." } }
        this.suggestedText =
          response?.data?.data?.optimized_text ||
          response?.data?.optimized_text ||
          "";

        if (!this.suggestedText) {
          throw new Error("La respuesta de la API no contiene texto optimizado");
        }

        // Mostrar modal de comparación
        this.$refs.comparisonModal.show();
      }  finally {
        this.isLoading = false;
      }
    },
    applySuggestion() {
      if (this.suggestedText) {
        this.$emit("input", this.suggestedText);
        this.$refs.comparisonModal.hide();
        
        // Feedback visual opcional
        Swal.fire({
          icon: "success",
          title: "Texto mejorado aplicado",
          timer: 1500,
          showConfirmButton: false,
          toast: true,
          position: "top-end",
        });
      }
    },
    discardSuggestion() {
      this.$refs.comparisonModal.hide();
    },
    resetModal() {
      this.originalText = "";
      this.suggestedText = "";
    },
  },
};
</script>

<style scoped>
.app-textarea-ai-wrapper {
  position: relative;
  width: 100%;
}

.textarea-container {
  position: relative;
  width: 100%;
}

.textarea-container textarea {
  width: 100%;
  padding-right: 45px; /* Espacio para el botón de IA */
  transition: all 0.2s ease;
}

.textarea-container textarea:focus {
  padding-right: 45px;
}

/* AI Button Styles */
.ai-button {
  position: absolute;
  bottom: 8px;
  right: 8px;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: linear-gradient(135deg, #5568d3 0%, #6a3d8f 100%);
  border: none;
  color: #ffffff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transform: scale(0.8);
  transition: all 0.2s ease;
  z-index: 10;
  box-shadow: 0 2px 8px rgba(102, 126, 234, 0.3);
}

.ai-button:hover:not(:disabled) {
  transform: scale(1.1);
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.5);
}

.ai-button:active:not(:disabled) {
  transform: scale(0.95);
}

.ai-button.visible {
  opacity: 1;
  transform: scale(1);
}

.ai-button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.ai-button i {
  font-size: 18px;
}

.ai-button .spinner-border-sm {
  width: 16px;
  height: 16px;
  border-width: 2px;
}

/* Comparison Modal Styles */
.comparison-container {
  display: flex;
  gap: 20px;
  min-height: 300px;
  margin: 20px 0;
}

.comparison-panel {
  flex: 1;
  display: flex;
  flex-direction: column;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 16px;
  background: #fafafa;
}

.panel-title {
  font-weight: 600;
  margin-bottom: 12px;
  color: #495057;
  display: flex;
  align-items: center;
  font-size: 14px;
}

.text-preview {
  flex: 1;
  padding: 12px;
  background: white;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  min-height: 200px;
  max-height: 400px;
  overflow-y: auto;
  white-space: pre-wrap;
  word-wrap: break-word;
  font-size: 14px;
  line-height: 1.6;
  color: #212529;
}

.original-text {
  border-left: 3px solid #6c757d;
}

.suggested-text {
  border-left: 3px solid #34c38f;
}

.comparison-divider {
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6c757d;
  font-size: 24px;
  padding: 0 10px;
}

.comparison-divider i {
  background: #f8f9fa;
  border-radius: 50%;
  padding: 8px;
}

/* Responsive */
@media (max-width: 768px) {
  .comparison-container {
    flex-direction: column;
    gap: 15px;
  }

  .comparison-divider {
    transform: rotate(90deg);
    padding: 10px 0;
  }

  .text-preview {
    min-height: 150px;
    max-height: 250px;
  }
}

/* Scrollbar styling for text preview */
.text-preview::-webkit-scrollbar {
  width: 6px;
}

.text-preview::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.text-preview::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 3px;
}

.text-preview::-webkit-scrollbar-thumb:hover {
  background: #555;
}
</style>
